import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Header from './Header';
import TaskModal from './TaskModal';
import ControlsSection from './ControlsSection';
import StatsBar from './StatsBar';
import KanbanBoard from './KanbanBoard';
import TaskList from './TaskList';
import { ViewMode } from './ViewToggle';
import { Task, TaskStats, ExtendedTaskFilter, CreateTaskData, UpdateTaskData, TaskImage } from '../types/Task';
import { taskApi, columnApi } from '../services/api';
import type { KanbanColumn } from '../services/api';
import { ThemeProvider } from '../contexts/ThemeContext';
import { filterTasks, sortTasks, getUniqueValues } from '../utils/filterUtils';
import ImagePreviewModal from './ImagePreviewModal';
import '../App.css';

export const TodoApp: React.FC = () => {
  const [allTasks, setAllTasks] = useState<Task[]>([]);
  const [stats, setStats] = useState<TaskStats | undefined>();
  const [columns, setColumns] = useState<KanbanColumn[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<ExtendedTaskFilter>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('kanban');
  const [availableStatuses, setAvailableStatuses] = useState<string[]>(['todo']);
  const [imagePreviewModal, setImagePreviewModal] = useState<{
    isOpen: boolean;
    images: TaskImage[];
    initialIndex: number;
  }>({
    isOpen: false,
    images: [],
    initialIndex: 0,
  });

  // Load all tasks (without server-side filtering)
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [tasksResponse, statsResponse, columnsResponse] = await Promise.all([
        taskApi.getTasks({}), // Load all tasks without filtering
        taskApi.getStats(),
        columnApi.getColumns()
      ]);
      
      setAllTasks(tasksResponse.tasks || []);
      setStats(statsResponse);
      setColumns(columnsResponse.columns);
      setAvailableStatuses(columnsResponse.columns.map((col: KanbanColumn) => col.status));
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load tasks. Please check if the backend server is running.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh data manually when needed (for example after major changes)
  const handleRefreshData = useCallback(async () => {
    console.log('🔄 Manual data refresh requested');
    await loadData();
  }, [loadData]);

  // Apply client-side filtering and sorting
  const filteredAndSortedTasks = useMemo(() => {
    const filtered = filterTasks(allTasks, filter);
    return sortTasks(filtered, filter);
  }, [allTasks, filter]);

  // Extract unique values for filter dropdowns
  const availableOwners = useMemo(() => getUniqueValues(allTasks, 'owner'), [allTasks]);
  const availableReporters = useMemo(() => getUniqueValues(allTasks, 'reportedBy'), [allTasks]);
  const availableLabels = useMemo(() => getUniqueValues(allTasks, 'labels'), [allTasks]);

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle creating a new task
  const handleCreateTask = async (taskData: CreateTaskData) => {
    try {
      const response = await taskApi.createTask(taskData);
      console.log('✅ Task created successfully:', response.id);
      
      // Dodaj nowy task do lokalnego stanu zamiast przeładowywać wszystko
      setAllTasks(prevTasks => [...prevTasks, response]);
      
      // Odśwież statystyki w tle (nie blokuj interfejsu)
      taskApi.getStats()
        .then(statsResponse => setStats(statsResponse))
        .catch(statsErr => console.warn('Failed to refresh stats:', statsErr));
      
      setIsModalOpen(false);
    } catch (err) {
      console.error('Failed to create task:', err);
      setError('Failed to create task');
    }
  };

  // Handle updating a task
  const handleUpdateTask = async (id: string, taskData: UpdateTaskData) => {
    try {
      const updatedTask = await taskApi.updateTask(id, taskData);
      console.log('✅ Task updated successfully:', id);
      
      // Zaktualizuj task w lokalnym stanie zamiast przeładowywać wszystko
      setAllTasks(prevTasks => 
        prevTasks.map(task => task.id === id ? updatedTask : task)
      );
      
      // Odśwież statystyki w tle (nie blokuj interfejsu)
      taskApi.getStats()
        .then(statsResponse => setStats(statsResponse))
        .catch(statsErr => console.warn('Failed to refresh stats:', statsErr));
      
      setIsModalOpen(false);
      setEditingTask(null);
    } catch (err) {
      console.error('Failed to update task:', err);
      setError('Failed to update task');
    }
  };

  // Handle modal save - wrapper for sync interface
  const handleModalSave = (data: CreateTaskData | UpdateTaskData) => {
    if (editingTask) {
      handleUpdateTask(editingTask.id, data as UpdateTaskData);
    } else {
      handleCreateTask(data as CreateTaskData);
    }
  };

  // Handle deleting a task
  const handleDeleteTask = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }

    try {
      await taskApi.deleteTask(id);
      console.log('✅ Task deleted successfully:', id);
      
      // Usuń task z lokalnego stanu zamiast przeładowywać wszystko
      setAllTasks(prevTasks => prevTasks.filter(task => task.id !== id));
      
      // Odśwież statystyki w tle (nie blokuj interfejsu)
      taskApi.getStats()
        .then(statsResponse => setStats(statsResponse))
        .catch(statsErr => console.warn('Failed to refresh stats:', statsErr));
    } catch (err) {
      console.error('Failed to delete task:', err);
      setError('Failed to delete task');
    }
  };

  // Handle opening new task modal
  const handleNewTask = () => {
    setEditingTask(null);
    setIsModalOpen(true);
  };

  // Handle editing a task
  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setIsModalOpen(true);
  };

  // Handle task images changed - refresh the specific task
  const handleTaskImagesChanged = async (taskId: string) => {
    try {
      const updatedTask = await taskApi.getTask(taskId);
      console.log('🖼️ Task images updated, refreshing task:', taskId);
      
      // Update the specific task in the local state
      setAllTasks(prevTasks => 
        prevTasks.map(task => task.id === taskId ? updatedTask : task)
      );
    } catch (err) {
      console.error('Failed to refresh task after image change:', err);
    }
  };

  // Handle closing modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTask(null);
  };

  // Handle filter changes
  const handleFilterChange = (newFilter: ExtendedTaskFilter) => {
    setFilter(newFilter);
  };

  // Handle status change in Kanban view
  const handleStatusChange = async (taskId: string, newStatus: string) => {
    try {
      const task = allTasks.find(t => t.id === taskId);
      if (!task) return;

      // Create update data with all required fields preserved
      const updateData: UpdateTaskData = {
        summary: task.summary, // Required field
        description: task.description,
        tag: task.tag,
        priority: task.priority,
        labels: task.labels,
        status: newStatus as any,
        videoTutorialTitle: task.videoTutorialTitle,
        videoTutorialLink: task.videoTutorialLink,
        owner: task.owner,
        reportedBy: task.reportedBy,
        expectedDate: task.expectedDate,
        originalEstimate: task.originalEstimate,
        actualTime: task.actualTime,
        startDate: task.startDate,
        endDate: task.endDate,
        workStartedAt: task.workStartedAt,
        workCompletedAt: task.workCompletedAt,
      };

      const updatedTask = await taskApi.updateTask(taskId, updateData);
      console.log('✅ Task status updated successfully:', taskId, newStatus);
      
      // Zaktualizuj task w lokalnym stanie zamiast przeładowywać wszystko
      setAllTasks(prevTasks => 
        prevTasks.map(t => t.id === taskId ? updatedTask : t)
      );
      
      // Odśwież statystyki w tle (nie blokuj interfejsu)
      taskApi.getStats()
        .then(statsResponse => setStats(statsResponse))
        .catch(statsErr => console.warn('Failed to refresh stats:', statsErr));
    } catch (err) {
      console.error('Failed to update task status:', err);
      setError('Failed to update task status');
    }
  };

  // Handle view mode change
  const handleViewModeChange = (newViewMode: ViewMode) => {
    setViewMode(newViewMode);
  };

  // Clear error
  const clearError = () => {
    setError(null);
  };

  // Handle image preview
  const handleImagePreview = async (task: Task) => {
    try {
      const images = await taskApi.getTaskImages(task.id);
      console.log('🖼️ Opening image preview for task:', task.id, 'Images:', images);
      
      if (images.images && images.images.length > 0) {
        setImagePreviewModal({
          isOpen: true,
          images: images.images,
          initialIndex: 0,
        });
      }
    } catch (error) {
      console.error('Failed to load task images:', error);
    }
  };

  // Close image preview modal
  const handleCloseImagePreview = () => {
    setImagePreviewModal({
      isOpen: false,
      images: [],
      initialIndex: 0,
    });
  };

  return (
    <ThemeProvider>
      <div className="app full-width">
        <Header onNewTask={handleNewTask} />
      
        <main className="main-content">
          <div className="container">
            <ControlsSection
              filter={filter}
              onFilterChange={handleFilterChange}
              viewMode={viewMode}
              onViewModeChange={handleViewModeChange}
              availableOwners={availableOwners}
              availableReporters={availableReporters}
              availableLabels={availableLabels}
              availableStatuses={availableStatuses}
              filteredCount={filteredAndSortedTasks.length}
              totalCount={allTasks.length}
            />
            
            {/* Results Counter */}
            <div className="results-counter">
              <div className="results-text">
                {Object.keys(filter).length > 0 ? (
                  <>
                    Showing <strong>{filteredAndSortedTasks.length}</strong> of <strong>{allTasks.length}</strong> tasks
                    {filteredAndSortedTasks.length !== allTasks.length && (
                      <span className="filter-indication"> (filtered)</span>
                    )}
                  </>
                ) : (
                  <>
                    Showing <strong>{allTasks.length}</strong> tasks
                  </>
                )}
              </div>
              {Object.keys(filter).length > 0 && (
                <div className="active-filters-summary">
                  <span className="filter-count">{Object.keys(filter).length} filter{Object.keys(filter).length !== 1 ? 's' : ''} active</span>
                </div>
              )}
            </div>
            
            <StatsBar stats={stats} />
            
            {error && (
              <div className="error-banner">
                <span>{error}</span>
                <button onClick={clearError} className="error-close">×</button>
              </div>
            )}

            {loading ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <span>Loading tasks...</span>
              </div>
            ) : (
              <div className="tasks-container">
                {filteredAndSortedTasks.length === 0 ? (
                  <div className="empty-state">
                    <h2>{allTasks.length === 0 ? 'No tasks found' : 'No tasks match your filters'}</h2>
                    <p>
                      {allTasks.length === 0 
                        ? 'Create your first task to get started'
                        : 'Try adjusting your filters or create a new task'
                      }
                    </p>
                    <button className="create-first-task-btn" onClick={handleNewTask}>
                      Create Task
                    </button>
                  </div>
                ) : viewMode === 'kanban' ? (
                  <KanbanBoard
                    tasks={filteredAndSortedTasks}
                    onEdit={handleEditTask}
                    onDelete={handleDeleteTask}
                    onStatusChange={handleStatusChange}
                    onImagePreview={handleImagePreview}
                  />
                ) : (
                  <TaskList
                    tasks={filteredAndSortedTasks}
                    columns={columns}
                    onEdit={handleEditTask}
                    onDelete={handleDeleteTask}
                    onImagePreview={handleImagePreview}
                  />
                )}
              </div>
            )}
          </div>
        </main>

        {isModalOpen && (
          <TaskModal
            task={editingTask}
            onSave={handleModalSave}
            onClose={handleCloseModal}
            existingTasks={allTasks}
            onTaskImagesChanged={handleTaskImagesChanged}
          />
        )}

        {imagePreviewModal.isOpen && (
          <ImagePreviewModal
            images={imagePreviewModal.images}
            initialIndex={imagePreviewModal.initialIndex}
            onClose={handleCloseImagePreview}
          />
        )}
      </div>
    </ThemeProvider>
  );
}; 